/** An answer's odds as a small table: what happens with the chance and without it, at year-end and counting the years after, and the odds-weighted line. Every figure is a share price. */
import type { RiskyChoices } from "./ownLimits";
import { formatUsdPerShare } from "../format";

type Chance = NonNullable<RiskyChoices[number]["chance"]>;
const cap = (t: string): string => `${t.charAt(0).toUpperCase()}${t.slice(1)}`;

/** The usual outcome's own prices, said beside the two outcomes: "Hold: $69.29 year-end, $55.68 years after". */
export interface OddsBaseline { readonly name: string; readonly yearEndUsdPerShare: number; readonly laterUsdPerShare?: number | undefined }

export function OddsTable({ chance, testId, baseline }: { readonly chance: Chance; readonly testId: string; readonly baseline?: OddsBaseline | undefined }) {
  const lt = chance.lookThrough;
  const p = chance.probability;
  const money = (v: number): string => formatUsdPerShare(v);
  const yearEndSame = money(chance.withUsdPerShare) === money(chance.withoutUsdPerShare);
  const laterSame = lt !== undefined && money(lt.withUsdPerShare) === money(lt.withoutUsdPerShare);
  if (yearEndSame && (lt === undefined || laterSame)) {
    return <span data-testid={testId}>{cap(chance.label)}<small>{`No effect on this year's price${lt === undefined ? "" : " or the years after"}.`}</small></span>;
  }
  const weighted = (a: number, b: number): number => p * a + (1 - p) * b;
  return (
    <div data-testid={testId}>
      <span>{cap(chance.label)}</span>
      <table className="gx-ev-odds">
        <caption className="gx-sr">Share price, $ a share</caption>
        <thead><tr><th scope="col" className="gx-sr">Outcome</th><th scope="col" className="gx-num" title="Price at year-end">Year-end</th>{lt === undefined ? null : <th scope="col" className="gx-num" title="Counting the years after">Years after</th>}</tr></thead>
        <tbody>
          <tr><th scope="row">If it happens</th><td className="gx-num">{money(chance.withUsdPerShare)}</td>{lt === undefined ? null : <td className="gx-num">{money(lt.withUsdPerShare)}</td>}</tr>
          <tr><th scope="row">If not</th><td className="gx-num">{money(chance.withoutUsdPerShare)}</td>{lt === undefined ? null : <td className="gx-num">{money(lt.withoutUsdPerShare)}</td>}</tr>
          <tr className="gx-ev-odds-w"><th scope="row">{`Weighted (${Math.round(p * 100)}%)`}</th><td className="gx-num">{money(weighted(chance.withUsdPerShare, chance.withoutUsdPerShare))}</td>{lt === undefined ? null : <td className="gx-num">{money(weighted(lt.withUsdPerShare, lt.withoutUsdPerShare))}</td>}</tr>
        </tbody>
      </table>
      {lt === undefined ? null : <small className="gx-ev-odds-base" data-testid={`${testId}.dollars`}>Years after: in today's dollars.</small>}
      {baseline === undefined ? null : <small className="gx-ev-odds-base" data-testid={`${testId}.baseline`}>{`${baseline.name}: ${money(baseline.yearEndUsdPerShare)} year-end${baseline.laterUsdPerShare === undefined ? "" : `, ${money(baseline.laterUsdPerShare)} years after`}`}</small>}
    </div>
  );
}
